/**
 * Pivot Helper
 * Transforms flat API data into pivoted table structure
 */

interface PivotConfig {
  rowKeys: string[]; // Fields to use as row identifiers (e.g., ['apptapPublisherId', 'apptapSiteId'])
  columnKey: string; // Field to use as column headers (e.g., 'date')
  valueKey: string; // Field to use as cell values (e.g., 'revenueApptap')
  labelKeys?: string[]; // Additional fields to include in row (e.g., ['apptapPublisherName', 'apptapSiteName'])
  expectedColumns?: string[]; // Optional: all expected column values to include even if no data (e.g., all dates in range)
}

interface PivotedData {
  data: any[];
  dateColumns: string[]; // The pivoted column names (dates)
}

/**
 * Pivot data from rows to columns
 *
 * Example input data:
 * [
 *   { apptapPublisherId: '1', apptapSiteId: 'A', date: '2025-10-21', revenueApptap: 100 },
 *   { apptapPublisherId: '1', apptapSiteId: 'A', date: '2025-10-20', revenueApptap: 150 },
 *   { apptapPublisherId: '2', apptapSiteId: 'B', date: '2025-10-21', revenueApptap: 200 }
 * ]
 *
 * Example output:
 * {
 *   data: [
 *     { apptapPublisherId: '1', apptapSiteId: 'A', '2025-10-21': 100, '2025-10-20': 150 },
 *     { apptapPublisherId: '2', apptapSiteId: 'B', '2025-10-21': 200, '2025-10-20': null }
 *   ],
 *   dateColumns: ['2025-10-21', '2025-10-20']
 * }
 */
export function pivotData(data: any[], config: PivotConfig): PivotedData {
  const { rowKeys, columnKey, valueKey, labelKeys = [], expectedColumns = [] } = config;

  // Collect all unique column values (dates) from the data
  const columnValues = new Set<string>();
  data.forEach(row => {
    if (row[columnKey]) {
      columnValues.add(row[columnKey]);
    }
  });

  // If expectedColumns is provided, use it to ensure all date columns are included
  // even if they have no data. Otherwise, use only the columns found in data.
  let sortedColumns: string[];
  if (expectedColumns.length > 0) {
    // Use the expected columns (already sorted in descending order by the caller)
    sortedColumns = expectedColumns;
  } else {
    // Sort column values (dates) found in data in descending order
    sortedColumns = Array.from(columnValues).sort((a, b) => b.localeCompare(a));
  }

  // Group data by row keys
  const grouped = new Map<string, any>();

  data.forEach(row => {
    // Create a unique key for this row
    const rowKey = rowKeys.map(key => row[key] || '').join('|');

    // Get or create the pivoted row
    if (!grouped.has(rowKey)) {
      const pivotedRow: any = {};

      // Add row key fields
      rowKeys.forEach(key => {
        pivotedRow[key] = row[key];
      });

      // Add label fields
      labelKeys.forEach(key => {
        if (row[key] !== undefined) {
          pivotedRow[key] = row[key];
        }
      });

      // Initialize all date columns with null
      sortedColumns.forEach(col => {
        pivotedRow[col] = null;
      });

      grouped.set(rowKey, pivotedRow);
    }

    // Set the value for this date column
    const pivotedRow = grouped.get(rowKey);
    const colValue = row[columnKey];
    if (colValue) {
      pivotedRow[colValue] = row[valueKey];
    }
  });

  return {
    data: Array.from(grouped.values()),
    dateColumns: sortedColumns
  };
}

/**
 * Calculate average across date columns
 * Excludes today's date if it's the most recent date (since today's data is incomplete)
 */
export function calculateRowAverage(row: any, dateColumns: string[], excludeToday: boolean = true): number | null {
  let sum = 0;
  let count = 0;

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  // Check if the most recent date (first in descending array) is today
  const mostRecentDate = dateColumns[0];
  const shouldExcludeMostRecent = excludeToday && mostRecentDate === today;

  dateColumns.forEach((col, index) => {
    // Skip the most recent date if it's today
    if (shouldExcludeMostRecent && index === 0) {
      return;
    }

    const val = row[col];
    if (val !== null && val !== undefined && val !== '') {
      const num = typeof val === 'string' ? parseFloat(val) : val;
      if (!isNaN(num)) {
        sum += num;
        count++;
      }
    }
  });

  return count > 0 ? sum / count : null;
}
